import { useTranslation } from 'react-i18next';
import type { ExceptionSeverity, IProductivityException } from '@interfaces/IProductivity';
import { StatusDot } from '@components/ui/StatusDot/StatusDot';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { AlertIcon, CheckCircleIcon, ChevronRightIcon } from '@utils/icons';
import { Card } from '../../shared/Card/Card';
import { CardHeader } from '../../shared/CardHeader/CardHeader';
import styles from './ExceptionsCenter.module.css';

/** Severity → dot colour token. Row tint/border are handled in CSS via
 *  the `data-severity` attribute. */
const SEVERITY_DOT: Record<ExceptionSeverity, string> = {
  high: 'var(--red)',
  medium: 'var(--amber)',
  low: 'var(--sidebar-accent)',
};

interface IExceptionsCenterProps {
  exceptions: IProductivityException[];
}

export function ExceptionsCenter({ exceptions }: IExceptionsCenterProps) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader
        icon={<AlertIcon size={18} />}
        title={t('productivity.exceptions.title')}
        meta={t('productivity.exceptions.window')}
      />

      {exceptions.length === 0 ? (
        <p className={styles.empty}>
          <span className={styles.emptyMark} aria-hidden="true">
            <CheckCircleIcon size={16} />
          </span>
          {t('productivity.exceptions.none')}
        </p>
      ) : (
        <ul className={styles.list}>
          {exceptions.map((exc) => (
            <li key={exc.id} className={styles.row} data-severity={exc.severity}>
              <StatusDot color={SEVERITY_DOT[exc.severity]} size={8} className={styles.dot} />
              <div className={styles.rowBody}>
                <div className={styles.rowHead}>
                  <span className={styles.rowTitle}>{t(`productivity.exceptions.cards.${exc.id}.title`)}</span>
                  {exc.meta.map((m, i) => (
                    <span key={`${exc.id}-${i}`} className={styles.metaChip}>
                      {m}
                    </span>
                  ))}
                  {exc.moreFloors && exc.moreFloors.length > 0 && (
                    <Tooltip content={exc.moreFloors.join(' · ')}>
                      <span className={styles.metaChip}>
                        {t('productivity.exceptions.moreFloors', { count: exc.moreFloors.length })}
                      </span>
                    </Tooltip>
                  )}
                </div>
                <p className={styles.rowDetail}>
                  {t(`productivity.exceptions.cards.${exc.id}.detail`, {
                    count: exc.count,
                    ...exc.figures,
                  })}
                </p>
              </div>

              <button
                type="button"
                className={styles.review}
                disabled
                title={t('productivity.exceptions.comingSoon')}
              >
                {t(`productivity.exceptions.cards.${exc.id}.action`)}
                <ChevronRightIcon size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}